import { useCallback, useRef, useState } from "react";
import { cn } from "@/shared/lib/utils";

const ABBREVIATIONS = new Map([
	["tc", "telephone conference"],
	["cw", "correspondence with"],
	["cwc", "correspondence with client"],
	["cwoc", "correspondence with opposing counsel"],
	["ra", "review and analysis of"],
	["drr", "draft and revise"],
	["depo", "deposition"],
	["msj", "motion for summary judgment"],
	["dm", "draft motion"],
	["comms", "communications with"],
	["docs", "documents"],
]);

const EXAMPLES = [
	{ abbr: "tc", expansion: "telephone conference" },
	{ abbr: "cwc", expansion: "correspondence with client" },
	{ abbr: "ra", expansion: "review and analysis of" },
	{ abbr: "msj", expansion: "motion for summary judgment" },
];

export function AbbreviationDemo() {
	const [value, setValue] = useState("");
	const [lastExpanded, setLastExpanded] = useState<string | null>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const textarea = textareaRef.current;
		if (!textarea) { setValue(e.target.value); return; }

		let text = e.target.value;
		const cursor = textarea.selectionStart;
		const before = text.slice(0, cursor);
		const after = text.slice(cursor);
		const match = before.match(/(\b\w+)\s$/);

		if (match) {
			const replacement = ABBREVIATIONS.get(match[1]);
			if (replacement) {
				const wordStart = before.lastIndexOf(match[1]);
				const newBefore = before.slice(0, wordStart) + replacement + " ";
				text = newBefore + after;
				setValue(text);
				setLastExpanded(match[1]);
				setTimeout(() => setLastExpanded(null), 1500);
				requestAnimationFrame(() => {
					textarea.setSelectionRange(newBefore.length, newBefore.length);
				});
				return;
			}
		}

		setValue(text);
	}, []);

	return (
		<div className="not-prose flex flex-col gap-4 rounded-lg border bg-muted/30 p-5">
			<div className="relative">
				<textarea
					ref={textareaRef}
					value={value}
					onChange={handleChange}
					rows={3}
					placeholder='Try typing "tc" then press Space...'
					className={cn(
						"flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors",
						lastExpanded && "ring-2 ring-primary/30",
					)}
				/>
				{lastExpanded && (
					<span className="absolute top-2 right-2 text-[10px] font-medium text-primary animate-pulse">
						Expanded!
					</span>
				)}
			</div>

			<div className="flex flex-wrap gap-1.5">
				{EXAMPLES.map(({ abbr, expansion }) => (
					<button
						key={abbr}
						type="button"
						onClick={() => {
							setValue(`${abbr} `);
							requestAnimationFrame(() => {
								const textarea = textareaRef.current;
								if (!textarea) return;
								textarea.focus();
								// Trigger the expansion by simulating the change
								const replacement = ABBREVIATIONS.get(abbr)!;
								setValue(`${replacement} `);
								setLastExpanded(abbr);
								setTimeout(() => setLastExpanded(null), 1500);
							});
						}}
						className="rounded-md border bg-background px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
					>
						<span className="font-mono">{abbr}</span>
						<span className="ml-1.5 text-[10px] opacity-60">{expansion}</span>
					</button>
				))}
			</div>

			<p className="text-center text-[11px] text-muted-foreground">
				Type an abbreviation then press{" "}
				<kbd className="rounded border bg-muted px-1 font-mono text-[10px]">Space</kbd>{" "}
				to expand — manage yours in Settings
			</p>
		</div>
	);
}
